/**
 * The plain-German readback of a condition. Moved here out of
 * editor-v2/utils/conditionSentence.ts so both halves of this codebase can
 * share ONE wording: editor-v2 (Vue 3 / vitest) imports it relatively, the
 * Vue-2 admin app (Jest, which cannot resolve `.ts` at all) imports it
 * normally -- the same reason contextRuleHelpers.js lives here.
 *
 * Plain JavaScript, no Vue, no dependency: everything this needs about a row
 * arrives in the row itself, and everything it needs about the FIELD behind a
 * row arrives through the caller's `resolve`. Knowing about field lists or
 * segments here would drag half the application into a module two worlds are
 * meant to share.
 *
 * German and English, from a small table here instead of a translation
 * layer: both worlds pass their UI language ('de' / 'en', or a locale such as
 * 'en-US') as an optional last argument; German is the default.
 *
 * A row is `{ path, operator, value?, kind, fieldType, segmentId?, raw? }`;
 * see ConditionRow in editor-v2/utils/conditionModel.ts. Type declarations
 * for TypeScript callers live in conditionSentence.d.ts -- runtime changes
 * here MUST be mirrored there.
 */

// The same words the form builder's logic editor uses (editor-v2 locale
// `forms.logic.comparators`), so a redakteur meets one vocabulary for one idea.
export const OPERATOR_WORDS = {
  '==': 'ist',
  '!=': 'ist nicht',
  in: 'enthält',
  '>': 'größer als',
  '>=': 'mindestens',
  '<': 'kleiner als',
  '<=': 'höchstens',
  '!!': 'ist beantwortet',
  '!': 'ist nicht beantwortet',
};

// The same words in English (editor-v2 locale en-US `forms.logic.comparators`).
export const OPERATOR_WORDS_EN = {
  '==': 'is',
  '!=': 'is not',
  in: 'contains',
  '>': 'greater than',
  '>=': 'at least',
  '<': 'less than',
  '<=': 'at most',
  '!!': 'is answered',
  '!': 'is not answered',
};

const WORDS = {
  de: {
    operators: OPERATOR_WORDS,
    verb: /^(ist\b|enthält\b)/,
    is: 'ist',
    empty: 'Diese Bedingung ist leer.',
    yes: 'Ja',
    no: 'Nein',
    numberLocale: 'de-DE',
    deletedField: 'ein gelöschtes Feld',
    deletedSegment: 'ein gelöschtes Segment',
    handwritten: 'eine von Hand geschriebene Bedingung zutrifft',
    segment: 'das Segment',
    met: 'ist erfüllt',
    notMet: 'ist nicht erfüllt',
    and: ' und ',
    or: ', oder wenn ',
  },
  en: {
    operators: OPERATOR_WORDS_EN,
    verb: /^(is\b|contains\b)/,
    is: 'is',
    empty: 'This condition is empty.',
    yes: 'Yes',
    no: 'No',
    numberLocale: 'en-US',
    deletedField: 'a deleted field',
    deletedSegment: 'a deleted segment',
    handwritten: 'a hand-written condition applies',
    segment: 'the segment',
    met: 'is met',
    notMet: 'is not met',
    and: ' and ',
    or: ', or if ',
  },
};

/** The word table for a language or locale ('en', 'en-US' -> English, anything else German). */
function wordsFor(language) {
  return String(language || 'de').startsWith('en') ? WORDS.en : WORDS.de;
}

/** The comparison words in a language, e.g. for a dropdown. */
export function operatorWords(language) {
  return wordsFor(language).operators;
}

// Each row brings its own verb ("Σ Gesamt ist kleiner als 50"), so a row reads
// the same on its own -- on the canvas chip -- as inside a longer sentence.
// A comparison word that is already a verb phrase keeps its words.
function verbPhrase(word, w) {
  return w.verb.test(word) ? word : `${w.is} ${word}`;
}

/** What an empty condition reads as. Callers that can say something more
 *  specific (editor-v2 knows such a rule is dropped on save) say it
 *  themselves before calling in -- a segment list drops nothing. */
export const EMPTY_SENTENCE = WORDS.de.empty;

const NUMBER_FIELD_TYPES = ['number', 'rating', 'nps', 'slider'];

// Yes/no fields store true/false, which is no word a redakteur reads.
const BOOLEAN_FIELD_TYPES = ['boolean', 'consent'];

function fieldFor(row, resolve) {
  return (typeof resolve === 'function' ? resolve(row) : null) || null;
}

/**
 * The value of a row as a reader of the sentence sees it: an option's label,
 * Ja/Nein for a yes/no field, a German-formatted number, else as stored.
 * Exported for the canvas chip, which shows one row in short.
 */
export function valueWord(row, resolve, language) {
  const w = wordsFor(language);
  if (BOOLEAN_FIELD_TYPES.indexOf(row.fieldType) !== -1 && (String(row.value) === 'true' || String(row.value) === 'false')) {
    return String(row.value) === 'true' ? w.yes : w.no;
  }
  const field = fieldFor(row, resolve);
  const options = field && field.options ? field.options : null;
  const option = options ? options.find((o) => o.value === row.value) : null;
  if (option) return option.label;
  // German thousands separators for NUMBER fields only. A text field whose
  // answer happens to look like a number is a string, and '12345' must come
  // through as typed rather than as '12.345'.
  if (row.value !== undefined && NUMBER_FIELD_TYPES.indexOf(row.fieldType) !== -1) {
    const n = Number(row.value);
    if (Number.isFinite(n)) return n.toLocaleString(w.numberLocale);
  }
  return row.value === undefined ? '' : String(row.value);
}

function fieldName(row, field, w) {
  if (field) return field.label;
  // The address bar has no field list to look one up in -- see
  // urlParameterGroup in editor-v2/utils/conditionFields.ts -- so the stored
  // parameter name IS the name here, same as it is the name the editor typed
  // when building the row.
  if (row.kind === 'urlParameter') return row.path;
  return w.deletedField;
}

function rowText(row, resolve, w) {
  if (row.raw !== undefined) return w.handwritten;

  if (row.kind === 'segment') {
    const field = fieldFor(row, resolve);
    const met = row.operator === '!=' ? w.notMet : w.met;
    return `${w.segment} ${field ? field.label : w.deletedSegment} ${met}`;
  }

  const name = fieldName(row, fieldFor(row, resolve), w);
  const word = verbPhrase(w.operators[row.operator] || row.operator, w);
  const value = valueWord(row, resolve, w === WORDS.en ? 'en' : 'de');
  return value === '' ? `${name} ${word}` : `${name} ${word} ${value}`;
}

/**
 * An AND-group's clause. Every readable row carries its own verb
 * ("A ist größer als X und B ist Y"); a hand-written row brings its own
 * finite verb too ("... Bedingung zutrifft"). Readable rows come first, the
 * hand-written ones after, all joined on "und".
 *
 * A hand-written row stored before its readable neighbours therefore reads
 * AFTER them here. That is deliberate, not a bug: "und" is commutative in
 * German, and only this display sentence reorders -- the stored rule keeps
 * every row in its original position and is never touched here.
 */
function groupText(group, resolve, w) {
  const readable = group.filter((row) => row.raw === undefined);
  const handwritten = group.filter((row) => row.raw !== undefined);
  const text = (rows) => rows.map((row) => rowText(row, resolve, w)).join(w.and);

  if (readable.length === 0) return text(handwritten);
  if (handwritten.length === 0) return text(readable);
  return `${text(readable)}${w.and}${text(handwritten)}`;
}

/**
 * The finished sentence for one condition.
 *
 * @param {object} input
 * @param {string} input.lead     Everything up to and including "wenn ",
 *                                e.g. 'Wird gezeigt, wenn ' or
 *                                'Trifft zu, wenn '. The caller owns it,
 *                                so a segment list does not talk about
 *                                showing an element.
 * @param {Array}  input.groups   OR-groups of AND-rows.
 * @param {Function} input.resolve  Given a row, returns `{ label, options? }`
 *                                for the field behind it, or null when that
 *                                field is gone. The caller decides what to
 *                                look a row up BY (a segment row by its
 *                                `segmentId`, everything else by its `path`).
 * @param {string} [input.language] 'de' (default) or 'en', or a locale such as 'en-US'.
 * @returns {string}
 */
export function sentenceFor(input) {
  const options = input || {};
  const lead = options.lead || '';
  const groups = options.groups || [];
  const { resolve } = options;
  const w = wordsFor(options.language);

  const parts = groups
    .filter((group) => group && group.length > 0)
    .map((group) => groupText(group, resolve, w));

  if (parts.length === 0) return w.empty;

  return `${lead}${parts.join(w.or)}.`;
}
